import React, { useEffect, useMemo, useRef, useState } from "react"
import {
  AspectRatio,
  Box,
  Button,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  DrawerProps,
  Flex,
  Image,
  Text,
  VStack,
} from "@chakra-ui/react"
import { Swiper, SwiperSlide } from "swiper/react"
import { Navigation } from "swiper"
import "swiper/css/navigation"

import { useAnalytics } from "@app/hooks/useAnalytics"
import { useCart } from "@app/hooks/useCart"
import { useRoutes } from "@app/hooks/useRoutes"
import { useShopify, useShopifyPrice } from "@app/hooks/useShopify"
import { useWorlds } from "@app/hooks/useWorlds"
import { useProductComputed } from "@app/hooks/useProductComputed"
import { useProductBadges } from "@app/hooks/useProductBadges"
import { useConfigContext } from "@app/providers/config"
import { useCartContext } from "@app/providers/cart"
import { getVariantAvailabilityState, availabilityMeta } from "@app/utils/availability"
import { scaleShopifyImage } from "@app/utils/image"

import Link from "@app/components/Link"
import Discount from "@app/components/Discount"
import ProductCardWishlist from "@app/components/ProductCard/ProductCardWishlist"
import ProductBackInStock from "@app/components/Product/ProductBackInStock"
import ProductBadge from "@app/components/Product/ProductBadge"
import { QuickAddVariantSwatch } from "@app/components/ProductCard/QuickAddVariantSwatch"
import { getHandle, getRelatedGroup, getSiblingHandles } from "@app/utils/product"

type QuickAddOption = {
  key: string
  product: any
  variant: any
  image?: { src?: string; alt?: string } | null
  label: string
}

type Props = {
  isOpen: boolean
  onClose: () => void
  item: any
  placement: DrawerProps["placement"]
  addToCartCallback?: (product: any, variant: any) => void
}

const QuickAddDrawer: React.FC<Props> = ({ isOpen, onClose, item, placement, addToCartCallback }) => {
  const { imageNormaliser, getProductsLight } = useShopify()
  const { currencyCode, id: cartId } = useCartContext()
  const { addToCart: addToCartStandard, loading: cartLoading } = useCart()
  const addToCart = addToCartCallback || addToCartStandard
  const { urlResolver } = useRoutes()
  const {
    settings: { routes },
  } = useConfigContext()
  const { getProductWorld } = useWorlds()
  const { getBadgesByTags } = useProductBadges()
  const { trackVariantSelect } = useAnalytics()

  // Sanity sibling groups only carry handles and publish flags, so fetch them live when the drawer
  // opens. They're the same handles the card's "X Options" count is worked out from.
  const siblingHandles = useMemo(() => getSiblingHandles(item), [item])
  const siblingKey = siblingHandles.join(",")
  const [liveSiblings, setLiveSiblings] = useState<any[]>([])
  const [siblingsLoading, setSiblingsLoading] = useState(siblingHandles.length > 0)

  // Keyed on the handles, not `item` or `getProductsLight`, which are new objects every render.
  useEffect(() => {
    setLiveSiblings([])
    setSiblingsLoading(siblingHandles.length > 0)
    if (!siblingHandles.length) return
    let active = true
    getProductsLight({ handles: siblingHandles, firstImages: 1, firstVariants: 10 })
      .then(products => active && setLiveSiblings(products.filter((sibling: any) => sibling?.handle)))
      // A failed fetch leaves no siblings, so the drawer falls back to the product's own variants.
      .catch(() => active && setLiveSiblings([]))
      .finally(() => active && setSiblingsLoading(false))
    return () => {
      active = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siblingKey])

  // Options = the sibling scent group (live Sanity siblings; Reactify hits carry no `siblings` key
  // and expose the same group as `related`), else the product's own variants. Each option is one
  // thumbnail with a representative variant.
  //
  // `item` and the live siblings are admin-normalised (variants carry `availableForSale`/`priceV2`/gid
  // ids), but Reactify `related` entries are Storefront-shaped (camelCase: `availableForSale`,
  // `inventoryQuantity`, gid `id`, `presentmentPrices`, `featuredImage.originalSrc`). They must NOT be
  // run through adminProductNormaliser, as that would clobber `availableForSale` (reads `.available`) and
  // double-encode the already-gid ids. Map them to the shape the drawer + addToCart need instead.
  const options: QuickAddOption[] = useMemo(() => {
    const toOption = (product: any): QuickAddOption => ({
      key: getHandle(product) || product?.id,
      product,
      variant: product?.variants?.find((v: any) => v?.availableForSale) || product?.variants?.[0],
      image: product?.images?.[0],
      label: product?.title,
    })
    const selfOption = toOption(item)

    // Show just the product until its siblings load, rather than flashing its own variants.
    if (siblingsLoading) return [selfOption]
    // If none of the siblings come back (query failed, or not on this channel), fall through to the
    // product's own variants below.
    if (liveSiblings.length) return [selfOption, ...liveSiblings.map(toOption)].filter(option => option?.variant)

    const siblings = getRelatedGroup(item)

    if (!siblings.length) {
      // Fall back to the product's own variants when there is no sibling scent group.
      const variants = item?.variants || []
      if (variants.length <= 1) return [selfOption]
      return variants.map((variant: any, index: number) => ({
        key: variant?.id || `${index}`,
        product: item,
        variant,
        image: item?.images?.[index] || item?.images?.[0],
        label: variant?.title,
      }))
    }

    const siblingOptions: QuickAddOption[] = siblings.map((sibling: any) => {
      const rawImages = sibling?.images?.length ? sibling.images : sibling?.featuredImage ? [sibling.featuredImage] : []
      const images = rawImages.map((image: any) => imageNormaliser(image))
      const variants = (sibling?.variants || []).map((v: any) => {
        const presentment =
          (v?.presentmentPrices || []).find((p: any) => p?.price?.currencyCode === currencyCode) || v?.presentmentPrices?.[0]
        return {
          ...v,
          id: v?.id || v?.storefrontId,
          availableForSale: v?.availableForSale,
          inventory_quantity: v?.inventoryQuantity ?? v?.inventory_quantity,
          priceV2: { amount: presentment?.price?.amount ?? v?.price, currencyCode: presentment?.price?.currencyCode || currencyCode },
          compareAtPriceV2: {
            amount: presentment?.compareAtPrice?.amount ?? v?.compareAtPrice,
            currencyCode: presentment?.compareAtPrice?.currencyCode || currencyCode,
          },
        }
      })
      const variant = variants.find((v: any) => v?.availableForSale) || variants[0]
      return {
        key: getHandle(sibling) || sibling?.id,
        product: { ...sibling, id: sibling?.id || sibling?.storefrontId, url: routes.PRODUCT, images, variants },
        variant,
        image: images[0],
        label: sibling?.title,
      }
    })

    // Dedupe by handle so the primary product isn't listed twice if it appears in the group.
    const seen = new Set<string>()
    return [selfOption, ...siblingOptions].filter(option => {
      const handle = getHandle(option?.product)
      if (handle && seen.has(handle)) return false
      if (handle) seen.add(handle)
      return option?.variant
    })
  }, [item, siblingsLoading, liveSiblings, imageNormaliser, currencyCode, routes.PRODUCT])

  // Seeded once on mount — the drawer remounts per open. Re-seeding on `item` snapped the shopper's
  // tile choice back mid-add: `useShopify` returns fresh normalisers each render, so `item` is a new
  // object on every cart-context change.
  const getFirstAvailableIndex = () =>
    Math.max(
      0,
      options.findIndex(option => option?.variant?.availableForSale)
    )
  const [selectedIndex, setSelectedIndex] = useState(getFirstAvailableIndex)
  const hasSelected = useRef(false)

  // Siblings usually load after mount, so re-seed once they settle (unless the shopper has already
  // picked a tile), or an out-of-stock product stays selected over an in-stock sibling.
  useEffect(() => {
    if (!siblingsLoading && !hasSelected.current) setSelectedIndex(getFirstAvailableIndex())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siblingsLoading])

  const [backInStockOpen, setBackInStockOpen] = useState(false)
  const [addToCartError, setAddToCartError] = useState(false)

  const selectedOption = options[selectedIndex] || options[0]
  const selectedProduct = selectedOption?.product
  const selectedVariant = selectedOption?.variant

  const { secondaryTitle, productSizeText, availabilityText } = useProductComputed(selectedProduct)
  const { onSale, price, formattedPrice, formattedCompareAtPrice } = useShopifyPrice(selectedVariant)
  const badges = useMemo(() => getBadgesByTags(selectedProduct?.tags || []), [getBadgesByTags, selectedProduct?.tags])

  const availabilityState = getVariantAvailabilityState(selectedVariant, selectedProduct)
  const { isNotify } = availabilityMeta(availabilityState)
  const isOutOfStock = availabilityState === "oos-no-action"
  const isInStore = availabilityState === "in-store"

  const world = useMemo(() => getProductWorld(selectedProduct), [getProductWorld, selectedProduct])
  const groupLabel = world?.name || selectedProduct?.title
  const productUrl = useMemo(() => urlResolver(selectedProduct, routes.PRODUCT)?.url, [urlResolver, selectedProduct, routes.PRODUCT])

  const productImages = (selectedProduct?.images || []).filter((image: any) => image?.src)
  const carouselImages = productImages.length ? productImages : selectedOption?.image?.src ? [selectedOption.image] : []

  const ctaLabel = isOutOfStock ? "Out of Stock" : isNotify ? "Notify Me" : "Add to Cart"
  const ctaDisabled = isOutOfStock || cartLoading
  const subtitle = secondaryTitle || productSizeText

  const handleCtaClick = async () => {
    if (isNotify) {
      setBackInStockOpen(true)
      return
    }
    if (ctaDisabled || !selectedVariant) return
    // Stay open through the mutation so the CTA's loading state is the feedback, then close on
    // success so this drawer's exit overlaps the cart flyout sliding in (useCart.addToCart
    // dispatches { type: "cart", payload: true } into the app provider once the cart comes back).
    // A failed add keeps the drawer open with the error above the CTA; useCart.addToCart silently
    // no-ops without a cart id, so a missing one is treated the same way.
    if (!cartId) {
      setAddToCartError(true)
      return
    }
    setAddToCartError(false)
    try {
      await addToCart(selectedProduct, selectedVariant, 1, [], "quick_add")
      onClose()
    } catch {
      setAddToCartError(true)
    }
  }

  return (
    <>
      <Drawer isOpen={isOpen} onClose={onClose} placement={placement} size="quickAdd">
        <DrawerOverlay />
        <DrawerContent>
          <DrawerHeader borderBottom="1px solid" borderColor="border.lightest">
            <Text
              variant="medium"
              fontSize={{ base: "14px", sm: "16px" }}
              textTransform="uppercase"
              letterSpacing="0.04em"
              color="primary.midGrey"
            >
              Select Option
            </Text>
          </DrawerHeader>
          <DrawerCloseButton top="14px" />

          <DrawerBody>
            <Flex direction={{ base: "row", sm: "column" }} gap={{ base: "12px", sm: "16px" }}>
              <Box position="relative" w={{ base: "120px", sm: "auto" }} mr={{ sm: -6 }} flexShrink={0}>
                <Box position="absolute" top="8px" right="8px" zIndex={2}>
                  <ProductCardWishlist product={selectedProduct} location="quick_add" />
                </Box>

                {isInStore && badges.length > 0 && <ProductBadge badges={badges} />}

                {/* Only the visible image path is mounted, so the other one's images are never downloaded.
                    `placement` is resolved by ProductCard — don't swap it for a breakpoint hook, see the note there. */}
                {placement === "right" ? (
                  // Desktop: image carousel — fixed 253x316 slides, the next one peeks past the drawer edge
                  // userSelect off: rapid arrow clicks register as a double-click and select the slide images
                  <Box
                    userSelect="none"
                    sx={{
                      WebkitTapHighlightColor: "transparent",
                      ".swiper": { width: "100%" },
                      ".swiper-slide": { height: "auto", width: "253px" },
                      ".swiper-button-next, .swiper-button-prev": {
                        color: "primary.midGrey",
                        background: "white",
                        borderRadius: "full",
                        width: "32px",
                        height: "32px",
                        boxShadow: "0 1px 4px rgba(0,0,0,0.15)",
                      },
                      ".swiper-button-next:after, .swiper-button-prev:after": { fontSize: "12px", fontWeight: "bold" },
                    }}
                  >
                    <Swiper
                      modules={[Navigation]}
                      navigation={carouselImages.length > 1}
                      slidesPerView={carouselImages.length > 1 ? "auto" : 1}
                      spaceBetween={0}
                    >
                      {carouselImages.map((image: any, index: number) => (
                        <SwiperSlide key={`quick-add-image-${index}`}>
                          <AspectRatio ratio={4 / 5} overflow="hidden" bg="grey.lightest">
                            <Image
                              src={scaleShopifyImage(image?.src, "550x700")}
                              alt={selectedProduct?.title}
                              objectFit="cover"
                              w="100%"
                              h="100%"
                              loading={index > 1 ? "lazy" : "eager"}
                            />
                          </AspectRatio>
                        </SwiperSlide>
                      ))}
                    </Swiper>
                  </Box>
                ) : (
                  // Mobile: single image thumbnail
                  <Box borderRadius="7px" overflow="hidden" bg="grey.lightest">
                    <AspectRatio ratio={4 / 5}>
                      <Image
                        src={scaleShopifyImage(carouselImages?.[0]?.src, "240x300")}
                        alt={selectedProduct?.title}
                        objectFit="cover"
                        w="100%"
                        h="100%"
                      />
                    </AspectRatio>
                  </Box>
                )}
              </Box>

              <VStack align="flex-start" spacing="4px" flex="1">
                {availabilityState === "coming-soon" && availabilityText && (
                  <Text size="sm" variant="medium" color="primary.midGrey">
                    {availabilityText}
                  </Text>
                )}

                <Box>
                  <Text
                    variant="plpCardTitle"
                    fontSize={{ base: "14px", sm: "20px" }}
                    lineHeight={{ base: "19.6px", sm: "28px" }}
                    letterSpacing={{ base: "-0.28px", sm: "-0.4px" }}
                  >
                    {selectedProduct?.title}
                  </Text>

                  {subtitle && (
                    <Text variant="larsseit" fontSize={{ base: "12px", sm: "14px" }} color="text.placeholder">
                      {subtitle}
                    </Text>
                  )}
                </Box>

                {price ? (
                  <Flex alignItems="center">
                    {onSale && (
                      <Text
                        variant="medium"
                        fontSize={{ base: "14px", sm: "18px" }}
                        lineHeight="1.4"
                        color="text.secondary"
                        textDecoration="line-through"
                        marginRight="4px"
                      >
                        {formattedCompareAtPrice}
                      </Text>
                    )}
                    <Text variant="medium" fontSize={{ base: "14px", sm: "18px" }} lineHeight="1.4" color="text.primary">
                      {formattedPrice}
                    </Text>
                  </Flex>
                ) : null}

                <Discount
                  tags={selectedProduct?.tags}
                  forceColor="text.sale"
                  size="sm"
                  lineHeight="16.8px"
                  mb="0"
                  sx={{ p: { fontSize: { base: "12px", sm: "14px" }, lineHeight: { base: "16.8px", sm: "19.6px" } } }}
                />

                {(availabilityState === "oos-no-action" || availabilityState === "oos-notify") && (
                  <Text size="sm" color="primary.midGrey">
                    Out of Stock
                  </Text>
                )}
              </VStack>
            </Flex>

            {options.length > 1 && (
              <Box mt="24px" pt="20px" borderTop="1px solid" borderColor="border.lightest">
                <Flex justifyContent="space-between" alignItems="center" mb="12px">
                  <Text variant="larsseit" fontSize={{ base: "12px", sm: "14px" }} color="primary.midGrey">
                    {groupLabel}
                  </Text>
                  {productUrl && (
                    <Link to={productUrl}>
                      <Text size="sm" color="primary.midGrey" textDecoration="underline">
                        View Product Page
                      </Text>
                    </Link>
                  )}
                </Flex>

                <Flex flexWrap="wrap" gap="8px">
                  {options.map((option, index) => {
                    const state = getVariantAvailabilityState(option.variant, option.product)
                    const meta = availabilityMeta(state)
                    return (
                      <QuickAddVariantSwatch
                        key={option.key}
                        image={option.image}
                        label={option.label}
                        isActive={index === selectedIndex}
                        isGreyed={meta.isGreyed}
                        isStrikethrough={meta.hasStrikethrough}
                        // All siblings (incl. OOS) are selectable so users can view the swatch's
                        // image + price; an OOS selection just disables the CTA to "Out of Stock".
                        isSelectable
                        onSelect={() => {
                          // A tap before siblings load shouldn't block the switch to an in-stock sibling.
                          if (!siblingsLoading) hasSelected.current = true
                          trackVariantSelect({
                            product: option.product,
                            variant: option.variant,
                            variantName: option.label,
                            location: "quick_add",
                          })
                          setSelectedIndex(index)
                        }}
                      />
                    )
                  })}
                </Flex>
              </Box>
            )}
          </DrawerBody>

          {/* In-store-only isn't shippable, so the Sanity badge over the image replaces the CTA. */}
          {!isInStore && (
            <DrawerFooter flexDirection="column" alignItems="stretch">
              {addToCartError && (
                <Text size="sm" color="text.error" mb={1}>
                  Something went wrong. Please try again.
                </Text>
              )}
              <Button
                variant="primary"
                size="large"
                width="100%"
                height="48px"
                pointerEvents={cartLoading ? "none" : "auto"}
                data-loading={cartLoading || undefined}
                disabled={ctaDisabled}
                onClick={handleCtaClick}
              >
                {ctaLabel}
              </Button>
            </DrawerFooter>
          )}
        </DrawerContent>
      </Drawer>

      {backInStockOpen && (
        <ProductBackInStock
          isOpen={backInStockOpen}
          onClose={() => setBackInStockOpen(false)}
          product={selectedProduct}
          variant={selectedVariant}
          location="quick_add"
        />
      )}
    </>
  )
}

export default React.memo(QuickAddDrawer)
